import './ColorPicker.css';
import React, { useLayoutEffect, useMemo, useRef, useState, useEffect } from 'react';
import { Box, IconButton, TextField, Button } from '@mui/material';
import EditOffIcon from '@mui/icons-material/EditOff';
import CloseIcon from '@mui/icons-material/Close';

interface ColorPickerProps {
  color: string;
  onChange?: (value: string, skipHistoryStack: boolean) => void;
  onClose?: () => void;
}

const basicColors = [
  '#ffffff',
  '#000000',
  '#d0021b',
  '#f5a623',
  '#f8e71c',
  '#8b572a',
  '#7ed321',
  '#417505',
  '#bd10e0',
  '#9013fe',
  '#4a90e2',
  '#50e3c2',
  '#b8e986',
  '#4a4a4a',
  '#9b9b9b',
];

const DEFAULT_COLOR = '';

export default function ColorPicker({ color, onChange, onClose }: ColorPickerProps) {
  const [selfColor, setSelfColor] = useState(transformColor('hex', color));
  const [inputColor, setInputColor] = useState(color);
  const [pendingColor, setPendingColor] = useState<string>(color);

  const saturationRef = useRef<HTMLDivElement>(null);
  const hueRef = useRef<HTMLDivElement>(null);

  const [saturationDimensions, setSaturationDimensions] = useState({
    width: 0,
    height: 0,
  });
  const [hueWidth, setHueWidth] = useState(0);

  useLayoutEffect(() => {
    const updateDimensions = () => {
      if (saturationRef.current) {
        const { width, height } = saturationRef.current.getBoundingClientRect();
        setSaturationDimensions({ width, height });
      }
      if (hueRef.current) {
        const { width } = hueRef.current.getBoundingClientRect();
        setHueWidth(width);
      }
    };

    const timer = setTimeout(() => {
      updateDimensions();
    }, 200);

    window.addEventListener('resize', updateDimensions);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateDimensions);
    };
  }, []);

  const saturationPosition = useMemo(() => {
    const x = clamp((selfColor.hsv.s / 100) * saturationDimensions.width, 0, saturationDimensions.width);
    const y = clamp(((100 - selfColor.hsv.v) / 100) * saturationDimensions.height, 0, saturationDimensions.height);
    return { x, y };
  }, [selfColor.hsv.s, selfColor.hsv.v, saturationDimensions]);

  const huePosition = useMemo(() => {
    const x = clamp((selfColor.hsv.h / 360) * hueWidth, 0, hueWidth);
    return { x };
  }, [selfColor.hsv.h, hueWidth]);

  const onSetHex = (hex: string) => {
    setInputColor(hex);
    if (hex === '') {
      setSelfColor(emptyColorObject());
      setPendingColor('');
      return;
    }
    if (/^#[0-9A-Fa-f]{6}$/i.test(hex)) {
      const newColor = transformColor('hex', hex);
      setSelfColor(newColor);
      setPendingColor(newColor.hex);
    } else {
      console.warn('Invalid HEX input:', hex);
    }
  };

  const onMoveSaturation = ({ x, y }: Position) => {
    if (saturationDimensions.width === 0 || saturationDimensions.height === 0) {
      return;
    }

    const s = (x / saturationDimensions.width) * 100;
    const v = 100 - (y / saturationDimensions.height) * 100;

    const newHsv = {
      ...selfColor.hsv,
      s: clamp(s, 0, 100),
      v: clamp(v, 0, 100),
    };

    const newColor = transformColor('hsv', newHsv);
    setSelfColor(newColor);
    setInputColor(newColor.hex);
    setPendingColor(newColor.hex);
  };

  const onMoveHue = ({ x }: Position) => {
    if (hueWidth === 0) {
      return;
    }

    const h = (x / hueWidth) * 360;
    const normalizedH = ((h % 360) + 360) % 360;

    const newHsv = { ...selfColor.hsv, h: normalizedH };
    const newColor = transformColor('hsv', newHsv);
    setSelfColor(newColor);
    setInputColor(newColor.hex);
    setPendingColor(newColor.hex);
  };

  useEffect(() => {
    if (color) {
      const newColor = transformColor('hex', color);
      setSelfColor(newColor);
      setInputColor(newColor.hex);
      setPendingColor(newColor.hex);
    } else {
      setSelfColor(emptyColorObject());
      setInputColor('');
      setPendingColor('');
    }
  }, [color]);

  const handleApply = () => {
    if (onChange && pendingColor.toLowerCase() !== color.toLowerCase()) {
      onChange(pendingColor, false);
      onClose?.();
    }
  };

  const handleReset = () => {
    setInputColor('');
    setSelfColor(emptyColorObject());
    setPendingColor('');
    onChange?.('', false);
  };

  return (
    <Box
      className='color-picker-wrapper'
      onClick={(e) => e.stopPropagation()}
      sx={{ position: 'relative' }}
    >
      {onClose && (
        <IconButton
          onClick={onClose}
          title='Close Color Picker'
          sx={{ position: 'absolute', top: '5px', right: '5px' }}
        >
          <CloseIcon />
        </IconButton>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <TextField
          label='Hex'
          variant='outlined'
          size='small'
          value={inputColor}
          onChange={(e) => onSetHex(e.target.value)}
          sx={{ flexGrow: 1 }}
        />
        <IconButton
          onClick={handleReset}
          title='Reset to Default Color'
          sx={{ ml: 1, mt: 4 }}
        >
          <EditOffIcon />
        </IconButton>
      </Box>
      <div className='color-picker-basic-color'>
        {basicColors.map((basicColor) => (
          <button
            key={basicColor}
            className={basicColor.toLowerCase() === selfColor.hex.toLowerCase() ? 'active' : ''}
            style={{ backgroundColor: basicColor }}
            onClick={() => {
              const newColor = transformColor('hex', basicColor);
              setInputColor(basicColor);
              setSelfColor(newColor);
              setPendingColor(newColor.hex);
            }}
          />
        ))}
      </div>
      <MoveWrapper
        ref={saturationRef}
        className='color-picker-saturation'
        style={{
          backgroundColor: `hsl(${selfColor.hsv.h}, 100%, 50%)`,
          pointerEvents: saturationDimensions.width === 0 ? 'none' : 'auto',
        }}
        onChange={onMoveSaturation}
      >
        <div
          className='color-picker-saturation_cursor'
          style={{
            backgroundColor: selfColor.hex,
            left: `${saturationPosition.x}px`,
            top: `${saturationPosition.y}px`,
          }}
        />
      </MoveWrapper>
      <MoveWrapper
        ref={hueRef}
        className='color-picker-hue'
        style={{
          pointerEvents: hueWidth === 0 ? 'none' : 'auto',
        }}
        onChange={onMoveHue}
      >
        <div
          className='color-picker-hue_cursor'
          style={{
            backgroundColor: `hsl(${selfColor.hsv.h}, 100%, 50%)`,
            left: `${huePosition.x}px`,
          }}
        />
      </MoveWrapper>
      <div
        className='color-picker-color'
        style={{ backgroundColor: selfColor.hex }}
      />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Button
          variant='contained'
          color='primary'
          onClick={handleApply}
          disabled={pendingColor.toLowerCase() === color.toLowerCase()}
        >
          Apply
        </Button>
      </Box>
    </Box>
  );
}

interface Position {
  x: number;
  y: number;
}

interface MoveWrapperProps {
  className?: string;
  style?: React.CSSProperties;
  onChange: (position: Position) => void;
  children: JSX.Element;
}

const MoveWrapper = React.forwardRef<HTMLDivElement, MoveWrapperProps>(function MoveWrapper(
  { className, style, onChange, children },
  ref
) {
  const draggedRef = useRef(false);

  const move = (e: React.MouseEvent | MouseEvent): void => {
    if (ref && typeof ref !== 'function' && ref.current) {
      const div = ref.current;
      const { width, height, left, top } = div.getBoundingClientRect();
      let x = e.clientX - left;
      let y = e.clientY - top;

      x = clamp(x, 0, width);
      y = clamp(y, 0, height);

      onChange({ x, y });
    }
  };

  const onMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    e.stopPropagation();

    move(e);

    const onMouseMove = (_e: MouseEvent) => {
      draggedRef.current = true;
      move(_e);
    };

    const onMouseUp = (_e: MouseEvent) => {
      document.removeEventListener('mousemove', onMouseMove, false);
      document.removeEventListener('mouseup', onMouseUp, false);
      move(_e);
      draggedRef.current = false;
    };

    document.addEventListener('mousemove', onMouseMove, false);
    document.addEventListener('mouseup', onMouseUp, false);
  };

  return (
    <div
      ref={ref}
      className={className}
      style={style}
      onMouseDown={onMouseDown}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>
  );
});

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

interface RGB {
  r: number;
  g: number;
  b: number;
}

interface HSV {
  h: number;
  s: number;
  v: number;
}

interface Color {
  hex: string;
  hsv: HSV;
  rgb: RGB;
}

export function toHex(value: string): string {
  if (!value.startsWith('#')) {
    const ctx = document.createElement('canvas').getContext('2d');
    if (!ctx) {
      throw new Error('2d context not supported or canvas already initialized');
    }
    ctx.fillStyle = value;
    return ctx.fillStyle;
  } else if (value.length === 4 || value.length === 5) {
    value = value
      .split('')
      .map((v, i) => (i ? v + v : '#'))
      .join('');
    return value;
  } else if (value.length === 7 || value.length === 9) {
    return value;
  }
  return '#000000';
}

function hex2rgb(hex: string): RGB {
  const rgbArr = (
    hex
      .replace(/^#?([a-f\d])([a-f\d])([a-f\d])$/i, (m, r, g, b) => '#' + r + r + g + g + b + b)
      .substring(1)
      .match(/.{2}/g) || []
  ).map((x) => parseInt(x, 16));

  return {
    r: rgbArr[0] || 0,
    g: rgbArr[1] || 0,
    b: rgbArr[2] || 0,
  };
}

function rgb2hsv({ r, g, b }: RGB): HSV {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;

  let h = 0;
  if (d === 0) {
    h = 0;
  } else if (max === r) {
    h = ((g - b) / d) % 6;
  } else if (max === g) {
    h = (b - r) / d + 2;
  } else if (max === b) {
    h = (r - g) / d + 4;
  }
  h *= 60;
  if (h < 0) h += 360;

  const s = max === 0 ? 0 : (d / max) * 100;
  const v = max * 100;

  return { h, s, v };
}

function hsv2rgb({ h, s, v }: HSV): RGB {
  s /= 100;
  v /= 100;
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;

  let rPrime = 0;
  let gPrime = 0;
  let bPrime = 0;

  if (0 <= h && h < 60) {
    rPrime = c;
    gPrime = x;
    bPrime = 0;
  } else if (60 <= h && h < 120) {
    rPrime = x;
    gPrime = c;
    bPrime = 0;
  } else if (120 <= h && h < 180) {
    rPrime = 0;
    gPrime = c;
    bPrime = x;
  } else if (180 <= h && h < 240) {
    rPrime = 0;
    gPrime = x;
    bPrime = c;
  } else if (240 <= h && h < 300) {
    rPrime = x;
    gPrime = 0;
    bPrime = c;
  } else if (300 <= h && h < 360) {
    rPrime = c;
    gPrime = 0;
    bPrime = x;
  }

  return {
    r: Math.round((rPrime + m) * 255),
    g: Math.round((gPrime + m) * 255),
    b: Math.round((bPrime + m) * 255),
  };
}

function rgb2hex({ r, g, b }: RGB): string {
  const clampRGB = (x: number) => clamp(x, 0, 255);
  return '#' + [clampRGB(r), clampRGB(g), clampRGB(b)].map((x) => x.toString(16).padStart(2, '0')).join('');
}

function emptyColorObject(): Color {
  return {
    hex: '',
    hsv: { h: 0, s: 0, v: 0 },
    rgb: { r: 0, g: 0, b: 0 },
  };
}

export function transformColor<M extends keyof Color, C extends Color[M]>(format: M, color: C): Color {
  if (format === 'hex' && typeof color === 'string' && color === '') {
    return emptyColorObject();
  }

  let hex: Color['hex'] = '#000000';
  let rgb: Color['rgb'] = { r: 0, g: 0, b: 0 };
  let hsv: Color['hsv'] = { h: 0, s: 0, v: 0 };

  if (format === 'hex') {
    const value = color as Color['hex'];
    hex = toHex(value);
    if (hex === '') {
      return emptyColorObject();
    }
    rgb = hex2rgb(hex);
    hsv = rgb2hsv(rgb);
  } else if (format === 'rgb') {
    const value = color as Color['rgb'];
    hex = rgb2hex(value);
    rgb = value;
    hsv = rgb2hsv(value);
  } else if (format === 'hsv') {
    const value = color as Color['hsv'];
    const validatedHsv = {
      h: isNaN(value.h) ? 0 : ((value.h % 360) + 360) % 360,
      s: isNaN(value.s) ? 0 : clamp(value.s, 0, 100),
      v: isNaN(value.v) ? 0 : clamp(value.v, 0, 100),
    };
    hsv = validatedHsv;
    rgb = hsv2rgb(hsv);
    // Clamp to ensure valid range
    rgb = {
      r: clamp(rgb.r, 0, 255),
      g: clamp(rgb.g, 0, 255),
      b: clamp(rgb.b, 0, 255),
    };
    hex = rgb2hex(rgb);
  }

  return { hex, hsv, rgb };
}
